import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ConfirmDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, CircleNotch, Moon } from "@phosphor-icons/react";

const EMPTY = { name: "", start_time: "08:00", end_time: "16:00", color: "#0047FF", rest_days: [], is_day_off: false };
const WEEKDAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const inputCls = "rounded-sm border-input bg-background text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";

export default function Turnos() {
  const [shifts, setShifts] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/shifts").then((r) => setShifts(Array.isArray(r.data) ? r.data : [])).catch((e) => {
      toast.error(apiError(e));
      setShifts([]);
    });
  }, []);

  useEffect(load, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, start_time: s.start_time, end_time: s.end_time,
      color: s.color || "#0047FF", rest_days: s.rest_days || [], is_day_off: !!s.is_day_off,
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/shifts/${editing.id}`, form);
        toast.success("Turno actualizado");
      } else {
        await api.post("/shifts", form);
        toast.success("Turno creado");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/shifts/${toDelete.id}`);
      toast.success("Turno eliminado");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleRestDay = (d) =>
    setForm((f) => ({ ...f, rest_days: f.rest_days.includes(d) ? f.rest_days.filter((x) => x !== d) : [...f.rest_days, d] }));

  if (!shifts) return <Loading label="Cargando turnos..." />;

  return (
    <div data-testid="turnos-page">
      <PageHeader title="Turnos" description="Define los turnos y sus horarios para asignarlos en los horarios semanales" testid="turnos-header">
        <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="new-shift-button" onClick={openCreate}>
          <Plus className="mr-2" size={15} /> Nuevo turno
        </Button>
      </PageHeader>

      {shifts.length === 0 ? (
        <EmptyState title="Sin turnos" description="Crea turnos como Mañana, Tarde o Noche para planificar los horarios." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="shifts-grid">
          {shifts.map((s) => (
            <div key={s.id} className="border border-border bg-card rounded-sm p-5 hover:border-foreground/40 hover:-translate-y-0.5 transition-[border-color,transform] duration-200"
              data-testid={`shift-card-${s.name}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-1.5 rounded-sm" style={{ backgroundColor: s.color }} />
                  <div>
                    <p className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
                      {s.name}
                      {s.is_day_off && <Moon size={14} className="text-muted-foreground" />}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {s.is_day_off ? "Día de descanso" : `${s.start_time} – ${s.end_time}`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent" data-testid={`shift-edit-${s.name}`} onClick={() => openEdit(s)}>
                    <PencilSimple size={15} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400 hover:bg-red-950/30" data-testid={`shift-delete-${s.name}`} onClick={() => setToDelete(s)}>
                    <Trash size={15} />
                  </Button>
                </div>
              </div>
              {s.rest_days?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {s.rest_days.map((d) => (
                    <span key={d} className="border border-border px-2 py-0.5 text-xs text-muted-foreground">
                      {typeof d === "number" ? WEEKDAYS[d]?.slice(0, 3) : String(d).slice(0, 3)}
                    </span>
                  ))}
                  <span className="text-xs text-muted-foreground ml-1">descanso</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">{editing ? "Editar turno" : "Nuevo turno"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="shift-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nombre *</Label>
              <Input className={inputCls} required data-testid="shift-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Hora inicio</Label>
                <Input type="time" className={inputCls} data-testid="shift-start-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} disabled={form.is_day_off} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Hora fin</Label>
                <Input type="time" className={inputCls} data-testid="shift-end-input" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} disabled={form.is_day_off} />
              </div>
            </div>
            <div className="flex items-center justify-between border border-border rounded-sm p-3">
              <Label className="text-sm text-foreground/80">Color del turno</Label>
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="h-8 w-14 cursor-pointer bg-transparent border border-input rounded-sm" data-testid="shift-color-input" />
            </div>
            <div className="flex items-center justify-between border border-border rounded-sm p-3">
              <Label className="text-sm text-foreground/80">Es día de descanso</Label>
              <Switch checked={form.is_day_off} onCheckedChange={(v) => setForm({ ...form, is_day_off: v })} data-testid="shift-dayoff-switch" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Días de descanso</Label>
              <div className="grid grid-cols-4 gap-2">
                {WEEKDAYS.map((d, i) => (
                  <label key={d} className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                    <Checkbox checked={form.rest_days.includes(i)} onCheckedChange={() => toggleRestDay(i)} className="border-input" data-testid={`rest-day-${i}`} />
                    {d.slice(0, 3)}
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="shift-save-button">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />}
                {editing ? "Guardar cambios" : "Crear turno"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}
        title="Eliminar turno" description={`Se eliminará el turno "${toDelete?.name}". Las asignaciones existentes podrían verse afectadas.`}
        onConfirm={remove} testid="delete-shift" />
    </div>
  );
}
